import { FC } from 'react';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { PersonOrderBy_Enum, PersonStatus_Enum } from '@weave/schema-gen-ts/dist/shared/persons/v3/enums.pb';
import { useInfiniteQuery } from 'react-query';
import { PersonAPI, PersonsV3 } from '@frontend/api-person';
import { InfinitePaginatedList } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useAppScopeStore } from '@frontend/scope';
import { useTaskCenterStore } from '@frontend/task-center-scope';
import { Avatar, SearchField, useDebouncedValue, useFormField, Text } from '@frontend/design-system';
import { listItemStyle, checkIconStyle } from './person-searchble-list.styles';
// File related constants, meant to be here
const PAGINATED_LIMIT = 20;
const DEFAULT_PICKER_HEIGHT = 220;

export interface PaginatedSearchableListProps {
  onSelect: (person: Person) => void;
  selectedPatient?: Person | null;
  selectedLocationId?: string;
}

export const PaginatedSearchableList: FC<PaginatedSearchableListProps> = ({
  onSelect,
  selectedPatient,
  selectedLocationId,
}) => {
  const { t } = useTranslation('task-center');
  const searchField = useFormField({ type: 'text', placeholder: t('Search patients') });
  const { locationIds } = useTaskCenterStore(['locationIds']);

  const { selectedLocationIds } = useAppScopeStore();
  const searchTerm = useDebouncedValue(searchField.value);

  const queryProps = useInfiniteQuery({
    queryKey: [selectedLocationIds, searchTerm, selectedLocationId],
    queryFn: ({ pageParam }) =>
      PersonAPI.getMultiPersonsV3({
        responseLimit: PAGINATED_LIMIT,
        pageToken: pageParam,
        search: searchTerm,
        orderBy: PersonOrderBy_Enum.FIRST_NAME,
        statusFilter: PersonStatus_Enum.ACTIVE,
        locationIds: selectedLocationId ? [selectedLocationId] : locationIds,
        sourceIds: [],
      }),
    getNextPageParam: (lastPage) => {
      if (lastPage.rows.length < PAGINATED_LIMIT) return undefined;
      return lastPage.nextOffset;
    },
  });

  return (
    <>
      <SearchField name={t('Patient picker search field')} {...searchField} />
      <InfinitePaginatedList
        infiniteQueryProps={queryProps}
        height={DEFAULT_PICKER_HEIGHT}
        renderListItem={({ listItem }) => {
          const name = PersonsV3.PersonHelpers.getFullName(listItem);
          return (
            <li key={listItem.personId} css={listItemStyle} onClick={() => onSelect(listItem)}>
              <Avatar name={name} size='xs' /> <Text size='large'>{name}</Text>
              {selectedPatient?.personId === listItem.personId && <Icon name='check' size={16} css={checkIconStyle} />}
            </li>
          );
        }}
        endOfListText=''
        emptyStateConfig={{
          disableGraphic: true,
          textProps: { title: t('No patients found') },
          description: t('Try adjusting your search '),
        }}
      />
    </>
  );
};
